/* prettier-ignore-file */
/**
 * Cash desk — "awaiting cash payment" confirmation screen. STAFF ONLY.
 *
 * Reached after the waiver is signed on a cash booking. At this point the booking
 * exists in `pendingBookings` with the signed waiver but NO payment: no money has
 * changed hands and it is not yet a real booking.
 *
 * Money is taken through the register in a completely separate process. This screen
 * exists so the person at the till can (a) see exactly what is owed, (b) enter what the
 * customer handed over and be told the change without doing mental arithmetic, and
 * (c) confirm — which is what promotes the pending record into a confirmed, paid booking.
 *
 * Nothing here talks to Stripe.
 */

import React, { useState, useEffect, useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  ActivityIndicator,
} from "react-native";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { doc, getDoc, setDoc, deleteDoc, Timestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, app } from "../firebase.config";
import AuthHeader from "../components/AuthHeader";
import Button from "../components/ui/Button";
import { useAuthStore, useSettings, useBookings, usePendingBookings, useProducts } from "../stores";
import { computeAvailabilityForRange } from "../utils/rentalAvailability";
import { getCollectionPath } from "../utils/dbPaths";
import { getTenantId, getStoreId } from "../services/userStoreCache";
import { formatCentsAsUSD, capitalizeWords } from "../utils/formatting";
import { buildCashPaymentEntry } from "../utils/createWizardBooking";
import { colors } from "../theme";

const functions = getFunctions(app);

/** Notes a till realistically holds, for one-tap tender. */
const QUICK_NOTES = [2000, 5000, 10000];

const CashPayment = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const location = useLocation();

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const authChecked = useAuthStore((state) => state.authChecked);
  const settings = useSettings((state) => state.settings);
  const bookings = useBookings((state) => state.bookings);
  const pendingBookings = usePendingBookings((state) => state.pendingBookings);
  const products1 = useProducts((state) => state.products1) || [];
  const products2 = useProducts((state) => state.products2) || [];

  const [booking, setBooking] = useState(null);
  const [pendingPath, setPendingPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [tenderText, setTenderText] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Staff only — this screen can mark a booking paid.
  useEffect(() => {
    if (authChecked && !isAuthenticated) navigate("/login");
  }, [authChecked, isAuthenticated, navigate]);

  // Load the pending booking. The path may come from the waiver hand-off; otherwise
  // fall back to the tenant/store path and finally the root collection.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const candidates = [
        location.state?.pendingBookingsPath,
        getCollectionPath("pendingBookings"),
        "pendingBookings",
      ].filter(Boolean);

      for (const path of candidates) {
        try {
          const snap = await getDoc(doc(db, path, bookingId));
          if (snap.exists()) {
            if (!cancelled) {
              setBooking(snap.data());
              setPendingPath(path);
              setLoading(false);
            }
            return;
          }
        } catch (error) {
          console.error(`⚠️ Could not read ${path}/${bookingId}:`, error);
        }
      }
      if (!cancelled) {
        setLoadError("This booking could not be found. It may already have been completed.");
        setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [bookingId, location.state]);

  const totalCents = Number(booking?.totalAmount) || 0;

  // Parse dollars-and-cents typed at the till into integer cents.
  const tenderCents = useMemo(() => {
    const cleaned = tenderText.replace(/[^0-9.]/g, "");
    if (!cleaned) return 0;
    const dollars = parseFloat(cleaned);
    return Number.isFinite(dollars) ? Math.round(dollars * 100) : 0;
  }, [tenderText]);

  const changeCents = tenderCents - totalCents;
  const isShort = tenderCents > 0 && changeCents < 0;
  const canConfirm = tenderCents >= totalCents && totalCents > 0 && !confirming && !cancelling;

  const setTenderCents = (cents) => setTenderText((cents / 100).toFixed(2));

  /** Round up to the next note above the total, e.g. $47.30 -> $50. */
  const roundUpSuggestions = useMemo(() => {
    if (!totalCents) return [];
    const suggestions = new Set();
    QUICK_NOTES.forEach((note) => {
      const rounded = Math.ceil(totalCents / note) * note;
      if (rounded >= totalCents) suggestions.add(rounded);
    });
    return [...suggestions].sort((a, b) => a - b).slice(0, 3);
  }, [totalCents]);

  /**
   * Items on this booking that are no longer available. Its own pending hold is
   * excluded, otherwise the booking would always appear to conflict with itself.
   * Returns [] when everything is still obtainable.
   */
  const checkAvailabilityNow = () => {
    if (!booking) return [];
    const availability = computeAvailabilityForRange({
      startDate: booking.startDate,
      endDate: booking.endDate,
      bookings,
      pendingBookings,
      products1Catalog: products1,
      products2Catalog: products2,
      excludeBookingId: bookingId,
    });

    const shortages = [];
    const check = (items, map) =>
      (items || []).forEach((item) => {
        const info = map?.[item.id];
        if (!info?.hasInventory) return; // untracked = unlimited
        if ((item.quantity || 0) > info.available) {
          shortages.push({ name: item.name || item.id, wanted: item.quantity, available: info.available });
        }
      });

    check(booking.product1, availability.product1);
    check(booking.product2, availability.product2);
    return shortages;
  };

  // Live, so a collision that happens while staff are at the register still surfaces.
  const liveShortages = useMemo(
    () => (booking ? checkAvailabilityNow() : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [booking, bookings, pendingBookings, products1, products2]
  );

  const handleConfirm = async () => {
    if (!canConfirm || !booking || !pendingPath) return;

    const confirmed = window.confirm(
      `Confirm ${formatCentsAsUSD(totalCents)} received in cash?\n\n` +
        `Tendered: ${formatCentsAsUSD(tenderCents)}\n` +
        `Change due: ${formatCentsAsUSD(Math.max(0, changeCents))}\n\n` +
        "This completes the booking."
    );
    if (!confirmed) return;

    setConfirming(true);
    try {
      // HARD BACKSTOP. A soft hold reduces collisions but cannot eliminate them, and a
      // cash booking can sit here for a while. Re-check availability at the moment of
      // confirmation, excluding this booking's own hold, and refuse rather than
      // silently overselling.
      const shortages = checkAvailabilityNow();
      if (shortages.length > 0) {
        const detail = shortages
          .map((s) => `• ${s.name}: need ${s.wanted}, ${s.available} available`)
          .join("\n");
        alert(
          "Cannot complete — these items were taken by another booking while this one " +
            `was waiting:\n\n${detail}\n\n` +
            "Adjust the booking or free up inventory before taking payment."
        );
        setConfirming(false);
        return;
      }

      const cashEntry = buildCashPaymentEntry({
        // The pending doc stores every cost field already; re-use them rather than
        // recomputing, so what is charged matches exactly what was quoted.
        quote: {
          finalTotalCents: totalCents,
          productCostCents: booking.productPriceAmount || 0,
          deliveryCostCents: booking.deliveryCostCents || 0,
          pickupCostCents: booking.pickupCostCents || 0,
          deliveryWithDiscountCents: booking.deliveryWithDiscount || 0,
          pickupWithDiscountCents: booking.pickupWithDiscount || 0,
          durationDiscountPercent: booking.durationDiscount?.discountPercent || 0,
          durationDiscountName: booking.durationDiscount?.discountName || "",
          durationDiscountCents: booking.durationDiscount?.discountCents || 0,
          transportationDiscountPercent: booking.transportationDiscount?.discountPercent || 0,
          transportationDiscountName: booking.transportationDiscount?.discountName || "",
          transportationDiscountCents: booking.transportationDiscount?.discountCents || 0,
          salesTaxCents: booking.salesTaxAmount || 0,
        },
        product1: booking.product1 || [],
        product2: booking.product2 || [],
      });

      // Record what was actually handed over. Useful for till reconciliation, and it
      // is the only place the change given is captured.
      cashEntry.tenderedCents = tenderCents;
      cashEntry.changeGivenCents = Math.max(0, changeCents);

      const tenantId = getTenantId();
      const storeId = getStoreId();
      const bookingsPath =
        tenantId && storeId ? `tenants/${tenantId}/stores/${storeId}/bookings` : "bookings";

      const finalBookingData = {
        ...booking,
        status: "confirmed",
        paymentMethod: "cash",
        payments: [cashEntry],
        updatedAt: Timestamp.now(),
      };
      // Only meaningful while awaiting payment.
      delete finalBookingData.tempCostBreakdown;

      await setDoc(doc(db, bookingsPath, bookingId), finalBookingData);
      await deleteDoc(doc(db, pendingPath, bookingId));

      try {
        const notify = httpsCallable(functions, "sendManualBookingNotifications");
        await notify({ bookingId, tenantId, storeId });
      } catch (error) {
        // A notification failure must never strand a booking staff just took money for.
        console.error("⚠️ Cash booking notifications failed:", error);
      }

      navigate(`/view-booking/${bookingId}`);
    } catch (error) {
      console.error("❌ Failed to complete cash booking:", error);
      alert(`Could not complete the booking: ${error.message || "Unknown error"}`);
      setConfirming(false);
    }
  };

  /**
   * Abandon the held booking entirely. Nothing has been charged and nothing has been
   * confirmed, so there is no booking record to flag as cancelled: the pending doc IS
   * the booking. Deleting it releases the inventory hold and drops it from the
   * "awaiting cash" strip. Irreversible, and the signed waiver goes with it, which is
   * why it sits behind a confirmation.
   */
  const handleCancelBooking = async () => {
    if (!booking || !pendingPath || confirming || cancelling) return;

    const name = capitalizeWords(
      `${booking.firstName || ""} ${booking.lastName || ""}`.trim()
    ) || "this customer";
    const confirmed = window.confirm(
      `Cancel the booking for ${name}?\n\n` +
        "The held booking and its signed waiver will be deleted and the items released. " +
        "No payment has been taken, so there is nothing to refund.\n\n" +
        "This cannot be undone."
    );
    if (!confirmed) return;

    setCancelling(true);
    try {
      await deleteDoc(doc(db, pendingPath, bookingId));
      navigate("/bookings");
    } catch (error) {
      console.error("❌ Failed to cancel pending cash booking:", error);
      alert(`Could not cancel the booking: ${error.message || "Unknown error"}`);
      setCancelling(false);
    }
  };

  if (!authChecked || loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.loadingText}>Loading booking…</Text>
      </View>
    );
  }

  if (!isAuthenticated) return null; // redirecting to /login

  if (loadError) {
    return (
      <View style={styles.container}>
        <AuthHeader title="Cash Payment" isDesktop={isDesktop} isAuthenticated={isAuthenticated} />
        <View style={styles.centered}>
          <Text style={styles.errorTitle}>Booking not found</Text>
          <Text style={styles.errorText}>{loadError}</Text>
          <Button title="Back to bookings" onPress={() => navigate("/bookings")} variant="secondary" />
        </View>
      </View>
    );
  }

  const customerName = capitalizeWords(
    `${booking.firstName || ""} ${booking.lastName || ""}`.trim()
  );

  return (
    <View style={styles.container}>
      <AuthHeader
        title="Cash Payment"
        subtitle={settings?.storeName?.name ? `by ${settings.storeName.name}` : ""}
        isDesktop={isDesktop}
        isAuthenticated={isAuthenticated}
        storeLogo={settings?.storeLogo?.url ?? ""}
      />

      <View style={isDesktop ? styles.desktopWrapper : styles.mobileWrapper}>
        <ScrollView style={styles.content}>
          {/* Surfaced BEFORE money changes hands. Discovering a shortage only at
              confirm time would mean staff had already taken the cash. */}
          {liveShortages.length > 0 && (
            <View style={styles.shortageBanner}>
              <Text style={styles.shortageTitle}>⚠️ Items no longer available</Text>
              {liveShortages.map((s) => (
                <Text key={s.name} style={styles.shortageItem}>
                  • {s.name}: need {s.wanted}, {s.available} available
                </Text>
              ))}
              <Text style={styles.shortageText}>
                Another booking took these while this one was waiting. Do not take payment
                until this is resolved.
              </Text>
            </View>
          )}

          <View style={styles.banner}>
            <Text style={styles.bannerTitle}>⏳ Awaiting cash payment</Text>
            <Text style={styles.bannerText}>
              The waiver is signed and this booking is being held. Take payment at the
              register, then confirm below to complete it.
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.label}>Customer</Text>
            <Text style={styles.customerName}>{customerName || "Unknown"}</Text>
            {booking.customerBookingID ? (
              <Text style={styles.meta}>Booking ID: {booking.customerBookingID}</Text>
            ) : null}
          </View>

          {/* Amount owed — the number the till needs, made unmissable. */}
          <View style={styles.totalCard}>
            <Text style={styles.totalLabel}>Total owed</Text>
            <Text style={styles.totalValue}>{formatCentsAsUSD(totalCents)}</Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.label}>Amount tendered</Text>
            <View style={styles.tenderRow}>
              <Text style={styles.currencyPrefix}>$</Text>
              <TextInput
                style={styles.tenderInput}
                value={tenderText}
                onChangeText={setTenderText}
                placeholder="0.00"
                placeholderTextColor={colors.ink300}
                keyboardType="decimal-pad"
                inputMode="decimal"
              />
            </View>

            <View style={styles.quickRow}>
              <TouchableOpacity
                dataSet={{ hover: "lift" }}
                style={styles.quickButton}
                onPress={() => setTenderCents(totalCents)}
              >
                <Text style={styles.quickButtonText}>Exact</Text>
              </TouchableOpacity>
              {roundUpSuggestions.map((cents) => (
                <TouchableOpacity
                  key={cents}
                  dataSet={{ hover: "lift" }}
                  style={styles.quickButton}
                  onPress={() => setTenderCents(cents)}
                >
                  <Text style={styles.quickButtonText}>{formatCentsAsUSD(cents)}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* The whole point of this screen: no mental arithmetic at the till. */}
            <View style={[styles.changeCard, isShort && styles.changeCardShort]}>
              <Text style={styles.changeLabel}>{isShort ? "Still owed" : "Change due"}</Text>
              <Text style={[styles.changeValue, isShort && styles.changeValueShort]}>
                {formatCentsAsUSD(Math.abs(changeCents))}
              </Text>
            </View>

            {isShort && (
              <Text style={styles.shortText}>
                Tendered amount is less than the total — collect the remainder before confirming.
              </Text>
            )}
          </View>

          <Button
            title="Confirm cash received & complete booking"
            onPress={handleConfirm}
            disabled={!canConfirm}
            loading={confirming}
            fullWidth
          />
          {!canConfirm && !confirming && (
            <Text style={styles.hint}>Enter an amount of at least the total owed to continue.</Text>
          )}

          {/* Destructive and irreversible, so it is the only red button here and the
              last thing on the page. */}
          <Button
            title="Cancel booking"
            onPress={handleCancelBooking}
            variant="danger"
            disabled={confirming}
            loading={cancelling}
            fullWidth
            style={styles.cancelBookingSpacing}
          />
          <Text style={styles.hint}>
            Deletes this held booking and its signed waiver and releases the items. No
            payment has been taken.
          </Text>

          <View style={styles.bottomSpacer} />
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg,
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  loadingContainer: { flex: 1, alignItems: "center", justifyContent: "center", minHeight: "100vh" },
  loadingText: { marginTop: 12, color: colors.ink500, fontSize: 14 },
  desktopWrapper: { width: "100%", maxWidth: 560, flex: 1, alignSelf: "center" },
  mobileWrapper: { flex: 1 },
  content: { flex: 1, padding: 16 },
  centered: { padding: 24, alignItems: "center" },
  errorTitle: { fontSize: 18, fontWeight: "bold", color: colors.ink700, marginBottom: 8 },
  errorText: { fontSize: 14, color: colors.ink500, textAlign: "center", marginBottom: 16 },

  banner: {
    backgroundColor: colors.warningSoft,
    borderLeftWidth: 4,
    borderLeftColor: colors.warning,
    borderRadius: 8,
    padding: 14,
    marginBottom: 12,
  },
  bannerTitle: { fontSize: 15, fontWeight: "700", color: colors.warningInk, marginBottom: 4 },
  bannerText: { fontSize: 13, color: colors.ink700, lineHeight: 18 },

  shortageBanner: {
    backgroundColor: colors.dangerSoft,
    borderLeftWidth: 4,
    borderLeftColor: colors.danger,
    borderRadius: 8,
    padding: 14,
    marginBottom: 12,
  },
  shortageTitle: { fontSize: 15, fontWeight: "700", color: colors.danger, marginBottom: 6 },
  shortageItem: { fontSize: 13, color: colors.ink700, paddingVertical: 1 },
  shortageText: { fontSize: 13, color: colors.ink700, lineHeight: 18, marginTop: 6 },

  section: {
    backgroundColor: colors.surface,
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
  },
  label: { fontSize: 12, color: colors.ink500, marginBottom: 6, textTransform: "uppercase" },
  customerName: { fontSize: 18, fontWeight: "700", color: colors.ink900 },
  meta: { fontSize: 13, color: colors.ink500, marginTop: 2 },

  totalCard: {
    backgroundColor: colors.primary,
    borderRadius: 8,
    padding: 20,
    marginBottom: 12,
    alignItems: "center",
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
  },
  totalLabel: { fontSize: 13, color: "rgba(255,255,255,0.85)", textTransform: "uppercase" },
  totalValue: { fontSize: 40, fontWeight: "800", color: colors.surface, marginTop: 4 },

  tenderRow: { flexDirection: "row", alignItems: "center" },
  currencyPrefix: { fontSize: 26, fontWeight: "700", color: colors.ink500, marginRight: 6 },
  tenderInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 14,
    fontSize: 26, // large for the till; also well clear of the iOS zoom threshold
    fontWeight: "700",
    color: colors.ink900,
    backgroundColor: colors.surface,
  },

  quickRow: { flexDirection: "row", flexWrap: "wrap", marginTop: 10 },
  quickButton: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    marginRight: 8,
    marginBottom: 8,
    minHeight: 44,
    justifyContent: "center",
    backgroundColor: colors.surface,
    cursor: "pointer",
  },
  quickButtonText: { fontSize: 14, fontWeight: "600", color: colors.ink700 },

  changeCard: {
    marginTop: 6,
    backgroundColor: colors.successSoft,
    borderRadius: 8,
    padding: 16,
    alignItems: "center",
  },
  changeCardShort: { backgroundColor: colors.dangerSoft },
  changeLabel: { fontSize: 12, color: colors.ink500, textTransform: "uppercase" },
  changeValue: { fontSize: 32, fontWeight: "800", color: colors.success, marginTop: 2 },
  changeValueShort: { color: colors.danger },
  shortText: { fontSize: 12, color: colors.danger, marginTop: 8 },

  hint: { fontSize: 12, color: colors.ink400, textAlign: "center", marginTop: 8 },
  cancelBookingSpacing: { marginTop: 28 },
  bottomSpacer: { height: 24 },
});

export default CashPayment;
